import { logger } from "@/utils/logger";
interface PageContext {
  url: string;
  h1_title: string;
  timestamp: number;
}

const STORAGE_KEY = 'context-current-page';

export const PageContextManager = {
  /**
   * Get current page context from localStorage
   */
  getCurrentPageContext(): PageContext | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading page context from localStorage:', error);
      return null;
    }
  },

  /**
   * Update page context in localStorage
   */
  updatePageContext(): void {
    try {
      const url = window.location.href;
      const h1Element = document.querySelector('h1');
      const h1_title = h1Element?.textContent?.trim() || 'No H1 title found';
      
      const context: PageContext = {
        url,
        h1_title,
        timestamp: Date.now()
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    } catch (error) {
      console.error('Error updating page context:', error);
    }
  },

  /**
   * Extract vehicle JSON from Schema.org data on the page
   * Returns null if multiple products detected (listing page)
   * Returns vehicle data only for single-product pages (VDP)
   */
  getVehicleSchemaData(): any | null {
    try {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      let productCount = 0;
      let firstProduct: any = null;
      
      for (const script of scripts) {
        try {
          const data = JSON.parse(script.textContent || '');
          const items = Array.isArray(data) ? data : [data];
          
          for (const item of items) {
            if (item['@type'] === 'Product' || item['@type'] === 'Vehicle') {
              productCount++;
              if (!firstProduct) {
                firstProduct = {
                  name: item.name,
                  description: item.description,
                  url: item.url,
                  price: item.offers?.price || item.price,
                  priceValidUntil: item.offers?.priceValidUntil || item.priceValidUntil,
                  itemCondition: item.offers?.itemCondition || item.itemCondition
                };
              }
              // Early exit if multiple products detected
              if (productCount > 1) {
                logger.log('📋 Multiple products detected - skipping vehicle context (listing page)');
                return null;
              }
            }
          }
        } catch (error) {
          // Skip invalid JSON
        }
      }
      
      if (productCount === 1 && firstProduct) {
        logger.log('📋 Single product detected - including vehicle context (VDP page)');
        return firstProduct;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Get formatted context message for AI
   */
  getContextMessage(language: 'en' | 'fr'): string {
    const context = this.getCurrentPageContext();
    if (!context) return '';

    const currentDateTime = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });

    let contextMessage = '';
    if (language === 'fr') {
      contextMessage = `Pour contexte, l'utilisateur consulte actuellement la page suivante : ${context.h1_title} et la date et l'heure actuelles sont : ${currentDateTime}`;
    } else {
      contextMessage = `For context, the user is currently viewing this page: ${context.h1_title} and the current date and time is: ${currentDateTime}`;
    }

    // Add vehicle JSON data if present
    const vehicleData = this.getVehicleSchemaData();
    if (vehicleData) {
      const formattedEntries = Object.entries(vehicleData)
        .filter(([_, value]) => value != null)
        .map(([key, value]) => {
          // Format name as "Vehicle"
          if (key === 'name') {
            return `Vehicle: ${value}`;
          }
          
          // Format price with currency symbol and commas
          if (key === 'price') {
            const numericPrice = parseFloat(String(value).replace(/[^\d.]/g, ''));
            if (!isNaN(numericPrice)) {
              return `vehicle price: $${numericPrice.toLocaleString()}`;
            }
            return `vehicle price: ${value}`;
          }
          
          // Format priceValidUntil
          if (key === 'priceValidUntil') {
            return `Price valid until: ${value}`;
          }
          
          // Format itemCondition and translate conditions
          if (key === 'itemCondition') {
            let condition = String(value);
            if (condition === 'NewCondition') {
              condition = 'New';
            } else if (condition === 'UsedCondition') {
              condition = 'Used';
            }
            return `Vehicle condition: ${condition}`;
          }
          
          // Keep other fields as-is
          return `${key}: ${value}`;
        });
      
      // Add Vehicle ID if present
      const topCarId = this.getTopCarId();
      if (topCarId) {
        formattedEntries.unshift(`Vehicle ID: ${topCarId}`);
      }
      
      // Add Vehicle VIN if present
      const vehicleVin = this.getVehicleVin();
      if (vehicleVin) {
        formattedEntries.push(`Vehicle VIN: ${vehicleVin}`);
      }
      
      // Add Exterior Colour if present
      const exteriorColor = this.getExteriorColor();
      if (exteriorColor) {
        formattedEntries.push(`Exterior Colour: ${exteriorColor}`);
      }
      
      const vehicleInfo = formattedEntries.join(', ');
      
      if (vehicleInfo) {
        if (language === 'fr') {
          contextMessage += `\n\nDonnées du véhicule de la page : ${vehicleInfo}`;
        } else {
          contextMessage += `\n\nVehicle data from page: ${vehicleInfo}`;
        }
      }
    }

    return contextMessage;
  },

  /**
   * Get topcarid from the page
   */
  getTopCarId(): string | null {
    try {
      const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
      return topCarIdElement?.value || null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Get VIN from the page
   */
  getVehicleVin(): string | null {
    try {
      const vinElement = document.querySelector('input[name="vin"]') as HTMLInputElement;
      return vinElement?.value || null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Get exterior color from the page
   */
  getExteriorColor(): string | null {
    try {
      const colorElement = document.querySelector('input[name="extcolor"]') as HTMLInputElement;
      return colorElement?.value || null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Get context for lead comments
   */
  getLeadContext(): string {
    const context = this.getCurrentPageContext();
    if (!context) return '';

    return `URL: ${context.url}\nPage title (Titre de la page): ${context.h1_title}`;
  }
};